import React from 'react'
import { Badge, badgeVariants } from '../../components/badge'
import { UITypography } from './ui-typography'
import { cn } from '../../lib/utils'
import { type VariantProps } from 'class-variance-authority'

export interface UIBadgeProps extends React.ComponentProps<typeof Badge>,
  VariantProps<typeof badgeVariants> {
  /**
   * 배지의 크기
   */
  size?: 'sm' | 'default' | 'lg'
  /**
   * 배지에 아이콘을 추가
   */
  icon?: React.ReactNode
  /**
   * 아이콘의 위치
   */
  iconPosition?: 'left' | 'right'
  /**
   * 점(dot) 표시 추가
   */
  dot?: boolean
  /**
   * 배지 모양
   */
  shape?: 'default' | 'pill'
}

const sizeVariants = {
  sm: "px-1.5 py-0.5 text-xs",
  default: "px-2 py-0.5 text-xs",
  lg: "px-3 py-1 text-sm"
}

const shapeVariants = {
  default: "rounded-md",
  pill: "rounded-full"
}

/**
 * 배지 컴포넌트
 * 다양한 크기, 아이콘, 모양 옵션을 제공하여 더 나은 시각적 표현을 지원합니다.
 */
export const UIBadge = React.forwardRef<
  React.ComponentRef<typeof Badge>,
  UIBadgeProps
>(({
  className,
  variant = "default",
  size = "default",
  icon,
  iconPosition = "left",
  dot = false,
  shape = "default",
  children,
  ...props
}, ref) => {
  const dotElement = dot && (
    <div className="w-1.5 h-1.5 rounded-full bg-current" />
  )

  const iconElement = icon && (
    <UITypography as="span" variant="small" className="flex-shrink-0">{icon}</UITypography>
  )

  return (
    <Badge
      ref={ref}
      variant={variant}
      className={cn(
        sizeVariants[size],
        shapeVariants[shape],
        dot && "gap-1.5",
        icon && "gap-1",
        className
      )}
      {...props}
    >
      {dot && dotElement}
      {icon && iconPosition === 'left' && iconElement}
      {children}
      {icon && iconPosition === 'right' && iconElement}
    </Badge>
  )
})

UIBadge.displayName = "UIBadge"

// Status-specific badge components
export const UIStatusBadge = React.forwardRef<
  React.ComponentRef<typeof UIBadge>,
  Omit<UIBadgeProps, 'variant'> & {
    status: 'active' | 'inactive' | 'pending' | 'error' | 'success' | 'warning'
  }
>(({ status, dot = true, ...props }, ref) => {
  const statusVariants = {
    active: { variant: 'success' as const, children: '활성' },
    inactive: { variant: 'secondary' as const, children: '비활성' },
    pending: { variant: 'warning' as const, children: '대기중' },
    error: { variant: 'destructive' as const, children: '오류' },
    success: { variant: 'success' as const, children: '성공' },
    warning: { variant: 'warning' as const, children: '경고' }
  }

  const statusConfig = statusVariants[status]

  return (
    <UIBadge
      ref={ref}
      variant={statusConfig.variant}
      dot={dot}
      {...props}
    >
      {props.children || statusConfig.children}
    </UIBadge>
  )
})

UIStatusBadge.displayName = "UIStatusBadge"